class ClickZoomLightbox extends HTMLElement {
  static #instance = null;

  connectedCallback() {
    if (ClickZoomLightbox.#instance && ClickZoomLightbox.#instance !== this) return;
    ClickZoomLightbox.#instance = this;

    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-hidden', 'true');

    this._config = {
      openDuration: 0.55,
      closeDuration: 0.45,
      openEase: 'power3.out',
      closeEase: 'power2.inOut',
      closeOnScroll: true,
      closeOnEscape: true,
      closeOnClick: true,
    };

    this._cloneEl = null;
    this._isOpen = false;
    this._isAnimating = false;
    this._openScrollY = 0;
    this._openSourceRectDoc = null;
    this._backdropColor = null;

    this._onDocClick = (e) => this._onDocumentClick(e);
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    this._detachCloseListeners();
    if (ClickZoomLightbox.#instance === this) ClickZoomLightbox.#instance = null;
  }

  _ensureBackdrop() {
    if (!this._backdropColor) {
      this._backdropColor = window.getComputedStyle(this).backgroundColor;
      this._transparent = 'rgba(0, 0, 0, 0)';
    }
  }

  _computeFlip(src, dst) {
    return {
      scaleX: src.width / dst.width,
      scaleY: src.height / dst.height,
      tx: src.left + src.width / 2 - (dst.left + dst.width / 2),
      ty: src.top + src.height / 2 - (dst.top + dst.height / 2),
    };
  }

  _open(img) {
    if (this._isOpen || this._isAnimating) return;
    if (!img.complete || !img.naturalWidth) return;

    if (typeof gsap === 'undefined') return;

    this._ensureBackdrop();
    this._isAnimating = true;
    this._openScrollY = window.scrollY;

    const srcRect = img.getBoundingClientRect();
    this._openSourceRectDoc = {
      top: srcRect.top + window.scrollY,
      left: srcRect.left,
      width: srcRect.width,
      height: srcRect.height,
    };

    const cloneEl = img.cloneNode(false);
    cloneEl.loading = 'eager';
    cloneEl.removeAttribute('data-click-zoom');
    this._cloneEl = cloneEl;

    const srcComputed = window.getComputedStyle(img);

    gsap.set(this, { display: 'flex', backgroundColor: this._transparent });

    const style = window.getComputedStyle(this);
    const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const aspect = srcRect.width / srcRect.height;
    const maxW = this.clientWidth - padX;
    const maxH = this.clientHeight - padY;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }

    gsap.set(cloneEl, {
      width: w,
      height: h,
      display: 'block',
      objectFit: srcComputed.objectFit || 'cover',
      objectPosition: srcComputed.objectPosition,
    });

    while (this.firstChild) this.removeChild(this.firstChild);
    this.appendChild(cloneEl);

    const dstRect = cloneEl.getBoundingClientRect();
    const flip = this._computeFlip(srcRect, dstRect);

    this.setAttribute('aria-hidden', 'false');
    document.documentElement.style.cursor = 'zoom-out';

    const tl = gsap.timeline({
      onComplete: () => {
        this._isAnimating = false;
        this._isOpen = true;
        this._attachCloseListeners();
      },
    });

    tl.to(this, { backgroundColor: this._backdropColor, duration: 0.3, ease: 'none' }, 0);
    tl.fromTo(
      cloneEl,
      { x: flip.tx, y: flip.ty, scaleX: flip.scaleX, scaleY: flip.scaleY },
      {
        x: 0,
        y: 0,
        scaleX: 1,
        scaleY: 1,
        duration: this._config.openDuration,
        ease: this._config.openEase,
      },
      0,
    );
  }

  _close() {
    if (!this._isOpen || this._isAnimating) return;
    this._isAnimating = true;
    this._detachCloseListeners();
    document.documentElement.style.cursor = '';

    const cloneEl = this._cloneEl;
    const dstRect = cloneEl.getBoundingClientRect();
    const startX = Number(gsap.getProperty(cloneEl, 'x')) || 0;
    const startY = Number(gsap.getProperty(cloneEl, 'y')) || 0;
    const startScaleX = Number(gsap.getProperty(cloneEl, 'scaleX')) || 1;
    const startScaleY = Number(gsap.getProperty(cloneEl, 'scaleY')) || 1;

    const srcRect = () => ({
      top: this._openSourceRectDoc.top - window.scrollY,
      left: this._openSourceRectDoc.left,
      width: this._openSourceRectDoc.width,
      height: this._openSourceRectDoc.height,
    });

    const cleanup = () => {
      gsap.set(this, { display: 'none', clearProps: 'backgroundColor' });
      if (cloneEl.parentNode) cloneEl.parentNode.removeChild(cloneEl);
      this._cloneEl = null;
      this.setAttribute('aria-hidden', 'true');
      this._openSourceRectDoc = null;
      this._isOpen = false;
      this._isAnimating = false;
    };

    const state = { t: 0 };

    gsap.to(state, {
      t: 1,
      duration: this._config.closeDuration,
      ease: this._config.closeEase,
      onUpdate: () => {
        const f = computeFlip(srcRect(), dstRect);
        const t = state.t;
        gsap.set(cloneEl, {
          x: startX + (f.tx - startX) * t,
          y: startY + (f.ty - startY) * t,
          scaleX: startScaleX + (f.scaleX - startScaleX) * t,
          scaleY: startScaleY + (f.scaleY - startScaleY) * t,
        });
      },
      onComplete: cleanup,
    });

    gsap.to(this, {
      backgroundColor: this._transparent,
      duration: 0.3,
      ease: 'power2.in',
      delay: this._config.closeDuration * 0.4,
    });
  }

  _onDocumentClick(e) {
    const article = e.target.closest('[data-click-zoom-article]');
    if (article) {
      const img = e.target.closest('img');
      if (img && article.contains(img)) {
        e.preventDefault();
        this._open(img);
        return;
      }
    }
    const trigger = e.target.closest('[data-click-zoom]');
    if (!trigger) return;
    const img = trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img');
    if (!img) return;
    e.preventDefault();
    this._open(img);
  }

  _attachCloseListeners() {
    this._boundOverlay = () => {
      if (this._config.closeOnClick) this._close();
    };
    this._boundKey = (e) => {
      if (this._config.closeOnEscape && e.key === 'Escape') this._close();
    };
    this._boundScroll = () => {
      if (!this._config.closeOnScroll) return;
      if (Math.abs(window.scrollY - this._openScrollY) < 2) return;
      this._close();
    };
    this.addEventListener('click', this._boundOverlay);
    document.addEventListener('keydown', this._boundKey);
    window.addEventListener('scroll', this._boundScroll, { passive: true });
  }

  _detachCloseListeners() {
    if (this._boundOverlay) this.removeEventListener('click', this._boundOverlay);
    if (this._boundKey) document.removeEventListener('keydown', this._boundKey);
    if (this._boundScroll) window.removeEventListener('scroll', this._boundScroll);
  }
}

function computeFlip(src, dst) {
  return {
    scaleX: src.width / dst.width,
    scaleY: src.height / dst.height,
    tx: src.left + src.width / 2 - (dst.left + dst.width / 2),
    ty: src.top + src.height / 2 - (dst.top + dst.height / 2),
  };
}

customElements.define('click-zoom-lightbox', ClickZoomLightbox);
